import { alpha, Box, Button, Checkbox, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { every, filter, includes, join, map, some, toLower, trim } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import CopyableText from 'components/atoms/CopyableText';
import { SlidePreviewCell } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import { HighlightedText } from 'components/atoms/HighlightedText';
import { Slide } from 'interfaces/slide';
import { DEFAULT_PAGE_SIZE } from 'utils/useEncodedFilters/constants';
import { useRangeSelection } from 'utils/useRangeSelection';

export interface SlideSelectionTableRow {
  assignment?: string;
  slide: Slide;
  sourceContext?: string;
  sourceContextItems?: string[];
  sourceLabId?: string;
  sourceLabName?: string;
}

interface SlideSelectionTableProps {
  disabledSlideIds?: string[];
  isLoading?: boolean;
  onSelectedSlideIdsChange: React.Dispatch<React.SetStateAction<string[]>>;
  /** Identifies the owning lab/study/source scope; changes reset range state and pagination, not selected IDs. */
  selectionScopeKey: string;
  rows: SlideSelectionTableRow[];
  selectedSlideIds: string[];
}

const matchesQuickFilter = (row: SlideSelectionTableRow, quickFilter: string) => {
  const normalizedFilter = toLower(trim(quickFilter));
  if (!normalizedFilter) {
    return true;
  }

  return some(
    [
      row.slide.id,
      row.slide.originalFileName,
      row.sourceContext,
      ...(row.sourceContextItems || []),
      row.sourceLabName,
      row.assignment,
    ],
    (value) => value && includes(toLower(String(value)), normalizedFilter)
  );
};

export const SlideSelectionTable: React.FC<SlideSelectionTableProps> = ({
  disabledSlideIds = [],
  isLoading = false,
  onSelectedSlideIdsChange,
  selectionScopeKey,
  rows,
  selectedSlideIds,
}) => {
  const [quickFilter, setQuickFilter] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const filteredRows = useMemo(() => filter(rows, (row) => matchesQuickFilter(row, quickFilter)), [quickFilter, rows]);
  const selectableSlideIds = useMemo(
    () =>
      filter(
        map(filteredRows, ({ slide }) => slide.id),
        (slideId) => !includes(disabledSlideIds, slideId)
      ),
    [disabledSlideIds, filteredRows]
  );
  const { clearSelectionPreview, previewedSelectedItems, previewSelection, resetRangeSelection, toggleSelection } =
    useRangeSelection({
      orderedItems: selectableSlideIds,
      selectedItems: selectedSlideIds,
      setSelectedItems: onSelectedSlideIdsChange,
    });
  const displayedSelectedSlideIds = previewedSelectedItems || selectedSlideIds;
  const allFilteredSlidesSelected =
    selectableSlideIds.length > 0 && every(selectableSlideIds, (slideId) => includes(selectedSlideIds, slideId));
  // Compare scope, filter, loading, and selectable ID order by value, not array identity.
  // Order matters for Shift ranges; disabled-ID ordering and duplicates do not change eligibility.
  // Exclude selected IDs and the local page so selection edits and page navigation preserve the anchor.
  const rangeSelectionKey = JSON.stringify([quickFilter, selectionScopeKey, selectableSlideIds, isLoading]);

  useEffect(() => {
    resetRangeSelection();
    setPaginationModel((currentModel) => ({ ...currentModel, page: 0 }));
  }, [rangeSelectionKey]);

  const toggleAllFilteredSlides = () => {
    if (isLoading) return;
    resetRangeSelection();
    onSelectedSlideIdsChange((currentSlideIds) =>
      allFilteredSlidesSelected
        ? filter(currentSlideIds, (slideId) => !includes(selectableSlideIds, slideId))
        : Array.from(new Set([...currentSlideIds, ...selectableSlideIds]))
    );
  };

  const columns = useMemo<GridColDef<SlideSelectionTableRow>[]>(
    () => [
      {
        field: 'selection',
        headerName: 'Select',
        sortable: false,
        filterable: false,
        width: 72,
        headerClassName: 'slide-selection-table-selection-header',
        cellClassName: 'slide-selection-table-selection-cell',
        renderCell: ({ row }) => {
          const slideId = row.slide.id;
          const disabled = isLoading || includes(disabledSlideIds, slideId);
          const selected = includes(selectedSlideIds, slideId);
          const displayedAsSelected = includes(displayedSelectedSlideIds, slideId);
          const selectionIsPreviewed = previewedSelectedItems !== undefined && selected !== displayedAsSelected;

          return (
            <Checkbox
              checked={displayedAsSelected}
              disabled={disabled}
              inputProps={{ 'aria-label': `Select ${row.slide.originalFileName || slideId}` }}
              onMouseEnter={(event) => previewSelection(slideId, event.shiftKey)}
              onMouseMove={(event) => previewSelection(slideId, event.shiftKey)}
              onMouseLeave={clearSelectionPreview}
              onClick={(event) => {
                event.stopPropagation();
                if (!disabled) toggleSelection(slideId, event.shiftKey);
              }}
              data-selection-preview={selectionIsPreviewed || undefined}
            />
          );
        },
      },
      {
        field: 'slide',
        headerName: 'Slide ID',
        flex: 1,
        minWidth: 260,
        sortable: false,
        renderCell: ({ row }) => (
          <Box onClick={(event) => event.stopPropagation()} sx={{ width: '100%' }}>
            <SlidePreviewCell
              slides={[row.slide]}
              labId={row.slide.labId}
              caseStudyId={row.slide.sourceStudyId}
              slidesMode
              summaryContent={<HighlightedText value={row.slide.id} query={quickFilter} />}
            />
          </Box>
        ),
      },
      {
        field: 'fileName',
        headerName: 'File name',
        flex: 1.2,
        minWidth: 220,
        sortable: false,
        valueGetter: (_value, row) => row.slide.originalFileName || '',
        renderCell: ({ row }) => <HighlightedText value={row.slide.originalFileName} query={quickFilter} />,
      },
      {
        field: 'sourceContext',
        headerName: 'Source',
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            {row.sourceLabId && row.sourceLabName ? (
              <CopyableText
                component="span"
                variant="body2"
                textForCopy={row.sourceLabId}
                tooltipText="Copy lab ID"
                copyWithButton="right"
                copyButtonVisibility="always"
                copyButtonProps={{
                  size: 'small',
                  'aria-label': `Copy lab ID for ${row.sourceLabName}`,
                  sx: { ml: 0.25, p: 0 },
                }}
                copyIconProps={{ fontSize: 'small' }}
                parentGridProps={{ sx: { width: 'auto' } }}
              >
                <HighlightedText value={`Source lab: ${row.sourceLabName}`} query={quickFilter} />
              </CopyableText>
            ) : null}
            {map(row.sourceContextItems || [row.sourceContext], (sourceContext) => (
              <HighlightedText key={sourceContext} value={sourceContext} query={quickFilter} />
            ))}
          </Stack>
        ),
      },
      {
        field: 'assignment',
        headerName: 'Assignment',
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => (
          <HighlightedText value={row.assignment || 'Needs case assignment'} query={quickFilter} />
        ),
      },
    ],
    [
      clearSelectionPreview,
      disabledSlideIds,
      displayedSelectedSlideIds,
      isLoading,
      previewedSelectedItems,
      previewSelection,
      quickFilter,
      selectedSlideIds,
      toggleSelection,
    ]
  );

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
        <TextField
          type="search"
          size="small"
          label="Filter slides"
          value={quickFilter}
          onChange={(event) => setQuickFilter(event.target.value)}
          sx={{ width: { xs: '100%', sm: 360 } }}
        />
        <Button
          size="small"
          color="secondary"
          onClick={toggleAllFilteredSlides}
          disabled={isLoading || !selectableSlideIds.length}
        >
          {allFilteredSlidesSelected ? 'Deselect All' : 'Select All'}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" aria-live="polite">
        {join(
          [
            `${filteredRows.length} result${filteredRows.length === 1 ? '' : 's'}`,
            `${selectedSlideIds.length} selected`,
          ],
          ' · '
        )}
      </Typography>
      <Box sx={{ height: 520, width: '100%' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.slide.id}
          loading={isLoading}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, DEFAULT_PAGE_SIZE, 50, 100]}
          getRowClassName={({ row }) =>
            includes(displayedSelectedSlideIds, row.slide.id) ? 'slide-selection-table-row--selected' : ''
          }
          sx={{
            '& .slide-selection-table-selection-header': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.24),
            },
            '& .slide-selection-table-selection-cell': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.06 : 0.18),
            },
            '& .slide-selection-table-row--selected .slide-selection-table-selection-cell': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.3),
            },
            '& .slide-selection-table-row--selected': {
              bgcolor: 'action.selected',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: -2,
            },
          }}
        />
      </Box>
    </Stack>
  );
};
